import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker, useLocation, useOutletContext } from 'react-router-dom';
import _isEqual from 'lodash/isEqual';
import { AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import { IconName } from '@lib/components/Modal/enums';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import TabBar from '@lib/components/TabBar/TabBar';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import getStatusFromError from '@lib/utils/getStatusFromError';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import { ContextProps } from 'hooks/useOutletContext';
import { CustomizeTabsModalForm } from './components/CustomizeTabsModalForm';
import { Widgets } from './components/Widgets';
import { HomePageActions } from './components/HomePageActions';
import WidgetModalForm from './components/WidgetModalForm/WidgetModalForm';
import useActions from './hooks/useActions';
import useWidgets from './hooks/useWidgets';
import useWidgetEditor from './hooks/useWidgetEditor';
import useHomePages from './hooks/useHomePages';
import useHomeNavigation from './hooks/useHomeNavigation';
import hasUnsavedChanges from './utils/hasUnsavedChanges';
import { HomeViewMode } from './enums';

interface HomeContainerProps {
  viewMode: HomeViewMode;
}

const modals = [
  Modals.HomeApplyTemplateModal,
  Modals.HomeCustomizeTabsModal,
  Modals.HomePublishModal,
  Modals.HomeWidgetModal,
];

/**
 * Container component for the home page dashboard
 * Displays tabs for different home pages and their widgets
 * Supports view mode and edit mode for customizing widgets
 */
export default function HomeContainer({ viewMode }: HomeContainerProps) {
  const isEditMode = viewMode === HomeViewMode.Edit;
  const { pathname } = useLocation();
  const { t } = useTranslation();

  // Centralized modal management
  const { isModalOpened, openModal, closeModal } = useModalManagement(modals);

  // Unsaved changes confirmation modal state
  const [isAwayModalOpen, setAwayModalOpen] = useState(false);

  // Get breadcrumbs data
  const breadCrumbsData = getBreadCrumbsDataByKey({ pathname, t });

  // Check user access permissions
  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.HomePageManagement) ===
    AccessLevelEnum.Read;

  // Get home pages data and tab state
  const {
    homePages,
    tableTabs,
    activeHomePageId,
    isActiveHomePageDefault,
    setActiveHomePageId,
    onTabChange,
    homePagesLoading,
    homePagesError,
    refetchHomePages,
  } = useHomePages(isEditMode);

  // Navigation handlers
  const {
    handleNavigateToEdit,
    handleNavigateToView,
    handleNavigateToWidgetTemplates,
  } = useHomeNavigation(activeHomePageId);

  // Get widgets for active home page
  const { widgets, widgetsLoading, widgetsError, refetchWidgets } = useWidgets({
    homePageId: activeHomePageId || undefined,
  });

  // Widget editor for managing local widget state
  // Called before useActions to provide setLocalWidgets/setLocalGrid
  const {
    localWidgets,
    localGrid,
    setLocalWidgets,
    setLocalGrid,
    saveLoading,
    editingWidget,
    widgetModalLoading,
    handleOpenWidgetModal,
    handleCloseWidgetModal,
    handleEditWidget,
    handleSubmitWidgetModal,
    handleRemoveWidget,
    initializeLocalWidgets,
    updateLayout,
    handleCancelEdit,
    handleSaveView,
  } = useWidgetEditor({
    homePageId: activeHomePageId || '',
    widgets,
    onExitEditMode: handleNavigateToView,
    refetchWidgets,
    openModal,
    closeModal,
  });

  // Track intentional navigation (after save/cancel) to bypass blocker
  const isIntentionalNavigationRef = useRef(false);

  // Block navigation when there are unsaved changes in edit mode
  const {
    proceed,
    state,
    reset: blockerReset,
  } = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isEditMode &&
      !widgetsLoading && // Don't block while loading
      !isIntentionalNavigationRef.current && // Don't block intentional navigation (save/cancel)
      hasUnsavedChanges(localWidgets, widgets, localGrid) &&
      !_isEqual(currentLocation.pathname, nextLocation.pathname),
  );

  // Wrapper for save action - shows confirmation modal for default pages
  const handleSaveClick = useCallback(() => {
    if (isActiveHomePageDefault) {
      openModal(Modals.HomePublishModal);
    } else {
      // Mark navigation as intentional before save
      isIntentionalNavigationRef.current = true;
      handleSaveView();
    }
  }, [isActiveHomePageDefault, openModal, handleSaveView]);

  // Confirm publish - saves and closes modal
  const handleConfirmPublish = useCallback(async () => {
    closeModal(Modals.HomePublishModal);
    // Mark navigation as intentional before save
    isIntentionalNavigationRef.current = true;
    await handleSaveView();
  }, [closeModal, handleSaveView]);

  // Handle confirmation to leave page with unsaved changes
  const onAwayConfirmed = useCallback(() => {
    setAwayModalOpen(false);
    // Cancel edit mode which resets local state
    handleCancelEdit();
    // Proceed with navigation
    proceed?.();
  }, [handleCancelEdit, proceed]);

  // Handle closing the away modal (stay on page)
  const onCloseAwayModal = useCallback(() => {
    setAwayModalOpen(false);
    // Reset the blocker to allow future navigation attempts
    if (blockerReset) blockerReset();
  }, [blockerReset]);

  // Wrapper for cancel action to mark navigation as intentional
  const handleCancelClick = useCallback(() => {
    handleCancelEdit();
  }, [handleCancelEdit]);

  const onOpenApplyTemplateModal = () =>
    openModal(Modals.HomeApplyTemplateModal);

  // Tab customization and edit mode actions
  const {
    handleApplyGeneralTemplate,
    handleSubmitCustomizeTabs,
    bulkUpdateLoading,
    bulkUpdateError,
  } = useActions({
    homePages,
    refetchHomePages,
    isViewOnly,
    activeHomePageId,
    setActiveHomePageId,
    localWidgets,
    setLocalWidgets,
    setLocalGrid,
    closeModal,
  });

  // Track if local widgets have been initialized in current edit session
  const isInitializedRef = useRef(false);

  // Reset flags when exiting edit mode
  useEffect(() => {
    if (!isEditMode) {
      isInitializedRef.current = false;
      isIntentionalNavigationRef.current = false;
    }
  }, [isEditMode]);

  // Initialize local widgets when entering edit mode and widgets query has completed
  useEffect(() => {
    if (isEditMode && !widgetsLoading && !isInitializedRef.current) {
      isInitializedRef.current = true;
      initializeLocalWidgets();
    }
  }, [isEditMode, widgetsLoading, initializeLocalWidgets]);

  // Show away confirmation modal when navigation is blocked
  useEffect(() => {
    if (state === 'blocked') {
      setAwayModalOpen(true);
    }
  }, [state]);

  // Cleanup blocker on unmount
  useEffect(
    () => () => {
      if (blockerReset) blockerReset();
    },
    [blockerReset],
  );

  // Show error page if home pages query failed
  if (homePagesError) {
    return (
      <ErrorPage
        status={getStatusFromError(homePagesError)}
        description={homePagesError?.message}
      />
    );
  }

  // Render settings button after tabs
  const renderAfterTabs = (
    <Tooltip body={t('customize-tabs')}>
      <IconButton
        icon="settings"
        type={IconButtonTypes.SecondaryFilled}
        symbolsOutlined
        rounded
        onClick={() => openModal(Modals.HomeCustomizeTabsModal)}
        disabled={isEditMode}
      />
    </Tooltip>
  );

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      <PageHeader title={t('home-page')}>
        <HomePageActions
          isActivePageDefault={isActiveHomePageDefault}
          isEditMode={isEditMode}
          isSaving={saveLoading}
          isViewOnly={isViewOnly}
          onAddWidget={handleOpenWidgetModal}
          onOpenApplyTemplateModal={onOpenApplyTemplateModal}
          onCancel={handleCancelClick}
          onCustomize={handleNavigateToEdit}
          onNavigateToWidgetTemplates={handleNavigateToWidgetTemplates}
          onSave={handleSaveClick}
        />
      </PageHeader>
      <PageBody>
        {homePagesLoading && !tableTabs.length ? (
          <CircleLoader />
        ) : (
          <>
            <TabBar
              items={tableTabs}
              active={activeHomePageId}
              onActiveChange={onTabChange}
              renderAfterTabs={renderAfterTabs}
            />
            {activeHomePageId && (
              <Widgets
                widgets={widgets}
                widgetsLoading={widgetsLoading}
                widgetsError={widgetsError}
                isViewOnly={isViewOnly}
                isEditMode={isEditMode}
                isDefault={isActiveHomePageDefault}
                onEnterEditMode={handleNavigateToEdit}
                onAddWidget={handleOpenWidgetModal}
                localWidgets={localWidgets}
                localGrid={localGrid}
                onLayoutChange={updateLayout}
                onEditWidget={handleEditWidget}
                onRemoveWidget={handleRemoveWidget}
                saveLoading={saveLoading}
              />
            )}
          </>
        )}
      </PageBody>
      <CustomizeTabsModalForm
        graphQLErrors={bulkUpdateError?.graphQLErrors}
        isOpen={isModalOpened(Modals.HomeCustomizeTabsModal)}
        isViewOnly={isViewOnly}
        loading={bulkUpdateLoading}
        onClose={() => closeModal(Modals.HomeCustomizeTabsModal)}
        onSubmit={handleSubmitCustomizeTabs}
        tabs={homePages}
      />
      <WidgetModalForm
        isOpen={isModalOpened(Modals.HomeWidgetModal)}
        onClose={handleCloseWidgetModal}
        onCancel={handleCloseWidgetModal}
        onSubmit={handleSubmitWidgetModal}
        isEditMode={!!editingWidget}
        editingWidget={editingWidget}
        loading={widgetModalLoading}
      />
      <ConfirmationModal
        confirmButtonLabel={t('home.yes-apply')}
        description={t('home.apply-general-template-description')}
        icon={IconName.Warning}
        isOpen={isModalOpened(Modals.HomeApplyTemplateModal)}
        onClose={() => closeModal(Modals.HomeApplyTemplateModal)}
        onConfirm={handleApplyGeneralTemplate}
        title={t('are-you-sure')}
      />
      <ConfirmationModal
        confirmButtonLabel={t('home.yes-save-and-publish')}
        description={
          <>
            <strong>{t('home.publish-changes-description-bold')}</strong>
            <br />
            {t('home.publish-changes-description')}
          </>
        }
        icon={IconName.Warning}
        isOpen={isModalOpened(Modals.HomePublishModal)}
        onClose={() => closeModal(Modals.HomePublishModal)}
        onConfirm={handleConfirmPublish}
        title={t('home.publish-changes-title')}
      />
      <ConfirmationModal
        confirmButtonLabel={t('leave-without-saving')}
        description={t('changes-are-not-saved-desc')}
        icon={IconName.Warning}
        isOpen={isAwayModalOpen}
        onClose={onCloseAwayModal}
        onConfirm={onAwayConfirmed}
        title={t('changes-are-not-saved')}
      />
    </>
  );
}
